import type { ReactNode } from 'react';
import type { TopbarButtonsMobilePlacement } from '../../components/Topbar/Topbar';

import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';
import { pageStore } from '../../lib/store/pageStore';

import Topbar from '../../components/Topbar/Topbar';

import { FlotiqBadgeLogo } from '../../images/shapes';

import SecondaryTopbar from './SecondaryTopbar/SecondaryTopbar';

const leftColumnClasses = 'xl:col-span-5';

export const predefinedLayoutClasses = {
  withSidebar: 'grid grid-cols-1 xl:grid-cols-7 gap-4 lg:gap-7 h-full',
  leftColumn: leftColumnClasses,
  leftColumnWhite: leftColumnClasses + ' bg-white dark:bg-slate-950 rounded-lg',
  rightColumn:
    'pt-4 xl:pt-0 border-t xl:border-t-0 dark:border-slate-800 xl:col-span-2 flex flex-col gap-5',
  whiteBox:
    'flex flex-col items-center justify-center h-full bg-white dark:bg-slate-950 rounded-lg p-4 mt-4',
  withDataGrid:
    'w-full h-auto md:h-[calc(100vh-140px)] lg:h-[calc(100vh-112px)] px-5 lg:px-7',
  dataGridContainer:
    'relative flex w-full h-auto min-h-0 flex-col overflow-visible rounded-lg mt-4 pb-6 md:h-full md:overflow-auto',
};

interface PageLayoutProps {
  /**
   * Page content
   */
  children: ReactNode;
  /**
   * Page title shown in helmet and topbar
   */
  title: string;
  /**
   * Topbar breadcrumbs
   */
  breadcrumbs?: ReactNode;
  /**
   * Topbar buttons
   */
  buttons?: ReactNode;
  /**
   * If topbar buttons are disabled
   */
  buttonsDisabled?: boolean;
  /**
   * Page id
   */
  id?: string;
  /**
   * Page sidebar key
   */
  page?: string;
  /**
   * Sidebar menu items that should be opened
   */
  menuItemOpen?: string;
  /**
   * Secondary topbar content
   */
  secondaryTopbar?: ReactNode;
  /**
   * Content object status
   */
  status?: ReactNode;
  /**
   * If page should not have default paddings
   */
  noPaddings?: boolean;
  /**
   * If sidebar should be hidden
   */
  hideSidebar?: boolean;
  /**
   * Placement of buttons on mobile devices
   */
  buttonsMobilePlacement?: TopbarButtonsMobilePlacement;
  /**
   * If the content area should fill the available viewport height and
   * scroll internally, instead of growing naturally with page scroll.
   */
  fillHeight?: boolean;
  /**
   * Component test id
   */
  testId?: string;
}

const PageLayout = ({
  title,
  breadcrumbs,
  buttons,
  buttonsDisabled = false,
  children,
  id,
  page,
  menuItemOpen,
  secondaryTopbar,
  status,
  noPaddings,
  hideSidebar = false,
  buttonsMobilePlacement = 'bottom',
  fillHeight = false,
  testId = '',
}: PageLayoutProps) => {
  useEffect(() => {
    pageStore.trigger.updatePage({
      id: id || page,
      hideSidebar,
      ...(page ? { page } : {}),
      ...(menuItemOpen ? { menuItemOpen } : {}),
    });
  }, [hideSidebar, id, menuItemOpen, page]);

  return (
    <div
      className={twMerge(
        'relative h-full w-full pb-10 lg:pb-0',
        status && 'pb-20',
      )}
    >
      <title>{title}</title>
      <Topbar
        topbarLogo={<FlotiqBadgeLogo className="h-10" />}
        topbarHeading={title || ''}
        buttons={buttons}
        buttonsDisabled={buttonsDisabled}
        status={status}
        breadcrumbs={breadcrumbs}
        buttonsMobilePlacement={buttonsMobilePlacement}
        {...getTestProps(testId, 'topbar', 'testId')}
      />
      <div
        className={twMerge(
          'flex items-stretch w-full min-h-[calc(100vh-71px)]',
          fillHeight && 'md:h-[calc(100vh-71px)] md:overflow-hidden',
        )}
      >
        <div
          className={twMerge(
            'flex flex-col w-full',
            fillHeight && 'md:h-full md:min-h-0',
          )}
        >
          <SecondaryTopbar>{secondaryTopbar}</SecondaryTopbar>
          <div
            className={twMerge(
              'h-full relative',
              !noPaddings && 'p-4 lg:p-7 ',
              fillHeight && 'md:flex-1 md:min-h-0 md:overflow-hidden',
            )}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PageLayout;
